import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { type ReactNode } from 'react'
import { Divider, SpinnerLoader, Typography, TotalDueTodayRow } from 'shared/ui'
import { TitledRow } from 'shared/ui/TitledRow/TitledRow'
import { type PriceInfoViewModel } from 'entities/Billing/model/PriceInfoViewModel'
import { getBillingLabel } from 'entities/Billing/lib/billingInterval'
import styles from './styles.module.scss'
export type IGracePeriodInfo = { totalSecondLine: ReactNode }

interface IPriceInfoProps {
  priceInfoStore: PriceInfoViewModel
  labelPrefix?: string
  gracePeriodInfo?: IGracePeriodInfo
  title?: ReactNode
}

export const PriceInfo = observer(
  ({ priceInfoStore, labelPrefix, gracePeriodInfo, title }: IPriceInfoProps) => {
    const {
      priceInfoUiData,
      requestInfoStatus: { status },
    } = priceInfoStore

    if (!priceInfoUiData) return null

    return (
      <div className={styles.info}>
        <SpinnerLoader
          ariaLabel={'PriceInfoSection'}
          wrapperClassName={classNames(styles.loading, {
            [styles.active]: status.isLoading,
          })}
        />
        {title}
        {labelPrefix &&
          priceInfoUiData.tierDetails?.map((details) => (
            <TitledRow
              key={details.tier}
              title={`${labelPrefix} × ${details.count}`}
              titleProps={{ color: 'var(--content-primary-primary)', variant: 'body-md-regular' }}
            >
              <Typography ariaLabel={'TierPrice'} variant={'body-md-regular'}>
                {priceInfoUiData.interval &&
                  `${details.perPrice} / ${getBillingLabel(priceInfoUiData.interval)}`}
              </Typography>
            </TitledRow>
          ))}

        <Divider sx={{ borderColor: 'var(--black-20)', marginTop: '4px', marginBottom: '6px' }} />

        <TitledRow
          title={'Subtotal'}
          titleProps={{ color: 'var(--content-primary-primary)', variant: 'body-md-regular' }}
        >
          <Typography ariaLabel={'Subtotal'} variant={'body-md-regular'}>
            {priceInfoUiData.subtotal}
          </Typography>
        </TitledRow>

        <TitledRow
          title={'Unused time'}
          titleProps={{ color: 'var(--content-primary-primary)', variant: 'body-md-regular' }}
        >
          <Typography ariaLabel={'UnusedTime'} variant={'body-md-regular'}>
            {`${priceInfoUiData.unusedTime}`}
          </Typography>
        </TitledRow>

        {priceInfoUiData.taxes && (
          <TitledRow
            title={`Sales tax - ${priceInfoUiData.taxes.jurisdiction} (${priceInfoUiData.taxes.percent}%)`}
            titleProps={{ color: 'var(--content-primary-primary)', variant: 'body-md-regular' }}
          >
            <Typography ariaLabel={'Taxes'} variant={'body-md-regular'}>
              {priceInfoUiData.taxes.total}
            </Typography>
          </TitledRow>
        )}

        <Divider sx={{ borderColor: 'var(--black-20)', margin: '4px 0 6px' }} />

        <TotalDueTodayRow
          totalValue={gracePeriodInfo?.totalSecondLine ? 0 : priceInfoUiData.totalDueToday}
          secondLine={gracePeriodInfo?.totalSecondLine}
          nextChargeDate={priceInfoUiData.noConvertedNextChargeDate}
          nextChargeAmountDollars={priceInfoUiData.nextCharge}
        />
      </div>
    )
  }
)
